import { useRef, useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Link as LinkIcon,
  Type,
  Palette,
  Minus,
  Undo2,
  Redo2,
  RemoveFormatting,
} from "lucide-react";

const FONT_SIZES = [
  { label: "Petit", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Moyen", value: "4" },
  { label: "Grand", value: "5" },
  { label: "Très grand", value: "6" },
];

const COLORS = [
  "#09090B", "#71717A", "#FFFFFF",
  "#EF4444", "#F97316", "#F59E0B",
  "#22C55E", "#3B82F6", "#8B5CF6",
  "#EC4899", "#14B8A6", "#6366F1",
];

function ToolbarButton({ icon: Icon, label, onClick, active, className }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            onClick();
          }}
          className={`
            w-8 h-8 flex items-center justify-center rounded-md transition-all duration-150
            ${active
              ? "bg-primary/10 text-primary"
              : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
            }
            ${className || ""}
          `}
          data-testid={`toolbar-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <Icon className="w-4 h-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p className="text-xs">{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export function RichTextEditor({ value, onChange, onFocus, isActive, editorRef: externalRef }) {
  const internalRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});
  const isInitializedRef = useRef(false);

  const editorEl = internalRef.current;

  // Sync external ref
  useEffect(() => {
    if (externalRef) {
      externalRef.current = internalRef.current;
    }
  }, [externalRef]);

  // Initialize content once
  useEffect(() => {
    if (internalRef.current && value && !isInitializedRef.current) {
      internalRef.current.innerHTML = value;
      isInitializedRef.current = true;
    }
  }, [value]);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const sel = window.getSelection();
    if (savedSelectionRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  }, []);

  const checkFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      justifyLeft: document.queryCommandValue("justifyLeft") === "true",
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight: document.queryCommandState("justifyRight"),
      justifyFull: document.queryCommandState("justifyFull"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
    });
  }, []);

  const execCmd = useCallback((command, val = null) => {
    restoreSelection();
    document.execCommand(command, false, val);
    saveSelection();
    checkFormats();
    if (internalRef.current) {
      onChange(internalRef.current.innerHTML);
    }
  }, [restoreSelection, saveSelection, checkFormats, onChange]);

  const handleInput = useCallback(() => {
    if (internalRef.current) {
      onChange(internalRef.current.innerHTML);
    }
    saveSelection();
    checkFormats();
  }, [onChange, saveSelection, checkFormats]);

  const handleEditorFocus = useCallback(() => {
    onFocus();
    checkFormats();
  }, [onFocus, checkFormats]);

  const handleKeyUp = useCallback(() => {
    saveSelection();
    checkFormats();
  }, [saveSelection, checkFormats]);

  const handleMouseUp = useCallback(() => {
    saveSelection();
    checkFormats();
  }, [saveSelection, checkFormats]);

  const handleInsertLink = useCallback(() => {
    const url = prompt("URL du lien :");
    if (url) {
      execCmd("createLink", url);
    }
  }, [execCmd]);

  // Convert an image URL to base64 data URI
  const imageUrlToBase64 = useCallback((url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        } catch {
          resolve(url);
        }
      };
      img.onerror = () => resolve(url);
      img.src = url;
    });
  }, []);

  // Convert a File/Blob to base64 data URI
  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }, []);

  // Process pasted HTML to convert all external images to base64
  const convertImagesToBase64 = useCallback(async (container) => {
    const images = container.querySelectorAll("img");
    for (const img of images) {
      const src = img.getAttribute("src") || "";
      if (src.startsWith("data:")) continue; // already base64

      if (src.startsWith("blob:")) {
        try {
          const resp = await fetch(src);
          const blob = await resp.blob();
          const dataUrl = await fileToBase64(blob);
          if (dataUrl) img.setAttribute("src", dataUrl);
        } catch {
          // can't convert blob, remove image
          img.remove();
        }
      } else if (src.startsWith("http://") || src.startsWith("https://")) {
        const dataUrl = await imageUrlToBase64(src);
        if (dataUrl && dataUrl.startsWith("data:")) {
          img.setAttribute("src", dataUrl);
        }
        // if conversion fails, keep original URL as fallback
      }
    }
  }, [fileToBase64, imageUrlToBase64]);

  // Handle paste to convert images
  const handlePaste = useCallback(async (e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Check for pasted files (direct image paste like screenshot)
    const files = Array.from(clipboardData.files || []);
    const imageFile = files.find((f) => f.type.startsWith("image/"));
    if (imageFile) {
      e.preventDefault();
      const dataUrl = await fileToBase64(imageFile);
      if (dataUrl) {
        document.execCommand("insertHTML", false, `<img src="${dataUrl}" style="max-width:100%;" />`);
        if (internalRef.current) onChange(internalRef.current.innerHTML);
      }
      return;
    }

    // Check for HTML content with images
    const html = clipboardData.getData("text/html");
    if (html && /<img\b/i.test(html)) {
      e.preventDefault();
      const temp = document.createElement("div");
      temp.innerHTML = html;

      // Convert all images to base64
      await convertImagesToBase64(temp);

      document.execCommand("insertHTML", false, temp.innerHTML);
      if (internalRef.current) onChange(internalRef.current.innerHTML);
      return;
    }
  }, [fileToBase64, convertImagesToBase64, onChange]);

  return (
    <TooltipProvider delayDuration={300}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`border rounded-xl overflow-hidden transition-all duration-200 bg-white ${
          isActive ? "ring-2 ring-primary/30 border-primary" : "border-zinc-200"
        }`}
        data-testid="rich-text-editor"
      >
        {/* Toolbar */}
        <div
          className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-zinc-100 bg-zinc-50/70"
          data-testid="editor-toolbar"
        >
          {/* Undo / Redo */}
          <ToolbarButton icon={Undo2} label="Annuler" onClick={() => execCmd("undo")} />
          <ToolbarButton icon={Redo2} label="Rétablir" onClick={() => execCmd("redo")} />

          <div className="w-px h-5 bg-zinc-200 mx-1" />

          {/* Text style */}
          <ToolbarButton icon={Bold} label="Gras" onClick={() => execCmd("bold")} active={activeFormats.bold} />
          <ToolbarButton icon={Italic} label="Italique" onClick={() => execCmd("italic")} active={activeFormats.italic} />
          <ToolbarButton icon={Underline} label="Souligné" onClick={() => execCmd("underline")} active={activeFormats.underline} />
          <ToolbarButton icon={Strikethrough} label="Barré" onClick={() => execCmd("strikeThrough")} active={activeFormats.strikeThrough} />

          <div className="w-px h-5 bg-zinc-200 mx-1" />

          {/* Font size */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="h-8 px-2 flex items-center gap-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-all text-xs font-medium"
                onMouseDown={(e) => e.preventDefault()}
                data-testid="toolbar-font-size"
              >
                <Type className="w-4 h-4" />
                Taille
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1" align="start">
              {FONT_SIZES.map((fs) => (
                <button
                  key={fs.value}
                  className="w-full text-left px-3 py-1.5 text-sm rounded-md hover:bg-zinc-100 transition-colors"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    execCmd("fontSize", fs.value);
                  }}
                  data-testid={`font-size-${fs.label.toLowerCase()}`}
                >
                  {fs.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Color */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="h-8 px-2 flex items-center gap-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-all text-xs font-medium"
                onMouseDown={(e) => e.preventDefault()}
                data-testid="toolbar-text-color"
              >
                <Palette className="w-4 h-4" />
                Couleur
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="start">
              <div className="grid grid-cols-6 gap-1.5">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    className="w-7 h-7 rounded-md border border-zinc-200 hover:scale-110 transition-transform active:scale-95"
                    style={{ backgroundColor: color }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      execCmd("foreColor", color);
                    }}
                    data-testid={`color-${color.replace("#", "")}`}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <div className="w-px h-5 bg-zinc-200 mx-1" />

          {/* Alignment */}
          <ToolbarButton icon={AlignLeft} label="Aligner à gauche" onClick={() => execCmd("justifyLeft")} active={activeFormats.justifyLeft} />
          <ToolbarButton icon={AlignCenter} label="Centrer" onClick={() => execCmd("justifyCenter")} active={activeFormats.justifyCenter} />
          <ToolbarButton icon={AlignRight} label="Aligner à droite" onClick={() => execCmd("justifyRight")} active={activeFormats.justifyRight} />
          <ToolbarButton icon={AlignJustify} label="Justifier" onClick={() => execCmd("justifyFull")} active={activeFormats.justifyFull} />

          <div className="w-px h-5 bg-zinc-200 mx-1" />

          {/* Lists */}
          <ToolbarButton icon={List} label="Liste à puces" onClick={() => execCmd("insertUnorderedList")} active={activeFormats.insertUnorderedList} />
          <ToolbarButton icon={ListOrdered} label="Liste numérotée" onClick={() => execCmd("insertOrderedList")} active={activeFormats.insertOrderedList} />

          <div className="w-px h-5 bg-zinc-200 mx-1" />

          {/* Link */}
          <ToolbarButton icon={LinkIcon} label="Insérer un lien" onClick={handleInsertLink} />

          {/* Horizontal rule */}
          <ToolbarButton icon={Minus} label="Ligne horizontale" onClick={() => execCmd("insertHorizontalRule")} />

          {/* Clear formatting */}
          <ToolbarButton icon={RemoveFormatting} label="Supprimer la mise en forme" onClick={() => execCmd("removeFormat")} />
        </div>

        {/* Editor area */}
        <div
          ref={internalRef}
          contentEditable
          suppressContentEditableWarning
          className="min-h-[280px] w-full p-5 focus:outline-none text-sm leading-relaxed text-zinc-800 [&_a]:text-primary [&_a]:underline"
          onInput={handleInput}
          onFocus={handleEditorFocus}
          onKeyUp={handleKeyUp}
          onMouseUp={handleMouseUp}
          onBlur={saveSelection}
          onPaste={handlePaste}
          data-testid="rich-text-editor-content"
          data-placeholder="Rédigez votre email ici..."
          style={{ minHeight: "280px", wordBreak: "break-word" }}
        />
      </motion.div>
    </TooltipProvider>
  );
}
